import { ArrowLeftIcon, BuildingOfficeIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import AccountSelector from '@/app/components/account/account-selector.tsx';
import AccountSiteStaffMemberForm from '@/app/components/staff/account-site-staff-member-form.tsx';
import { useAuth } from '@/app/hooks/auth-provider';
import toast from '@/app/toast.ts';
import { Field, FieldGroup, Fieldset } from '@/primitives/fieldset.tsx';
import { Heading, Subheading } from '@/primitives/heading.tsx';
import { Text } from '@/primitives/text.tsx';
import { useFetchAccountSiteStaffMemberForStaffQuery } from '@/slices/account-site-staff-member-api-slice.ts';
import { useSecurityContext } from '../../../hooks/security-provider.tsx';
import type { AccountListModel } from '../../../types/account.type.ts';
import type { AccountSiteStaffMemberModel } from '../../../types/account-site-staff-member.type.ts';
import type { StaffMemberListModel, StaffMemberModel } from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';

interface StaffAccountsProps {
  staff: StaffMemberListModel;
  previousStep?: () => void;
}

const StaffAccounts: FC<StaffAccountsProps> = ({ staff, previousStep }) => {
  const { currentAccount } = useSecurityContext();
  const { user } = useAuth();
  const { data } = useFetchAccountSiteStaffMemberForStaffQuery(staff?.key ?? skipToken);
  const [newAccount, setNewAccount] = useState<AccountSiteStaffMemberModel | null>(null);
  const form = useForm<{ account: AccountListModel }>({
    values: { account: currentAccount },
  });

  const { handleSubmit } = form;

  const onSubmit: SubmitHandler<{ account: AccountListModel }> = (value: {
    account: AccountListModel;
  }) => {
    if (data?.some(x => x.account.id === value.account.id)) {
      toast.error(
        'The user already has access to this account. Please select a different account.',
      );
    } else {
      setNewAccount({
        account: value.account,
        staffMember: staff,
        staffMemberUserId: staff.id,
        specialties: [],
      } as AccountSiteStaffMemberModel);
    }
  };

  const canAddAccounts =
    user.userType === UserTypeEnum.Admin ||
    (user.userType === UserTypeEnum.StaffMember &&
      (user as StaffMemberModel).accountSiteStaffMembers.length > 1);

  return (
    <div className='flex flex-col gap-6'>
      {canAddAccounts && (
        <div className='bg-white rounded-xl shadow-md border border-gray-300 p-6 max-w-screen-md'>
          <Subheading>Add Accounts</Subheading>
          <Text className='mb-4'>
            Manage the accounts this staff member can access and configure their role and
            permissions for each account.
          </Text>
          <FormProvider {...form}>
            <form className='flex flex-col sm:flex-row gap-4' onSubmit={handleSubmit(onSubmit)}>
              <Fieldset className='flex-1'>
                <FieldGroup>
                  <Field>
                    <AccountSelector
                      required={true}
                      name='account'
                      blackList={data?.map(x => x.account) ?? []}
                    />
                  </Field>
                </FieldGroup>
              </Fieldset>
              <div className='flex items-end'>
                <Button color='primary' type='submit'>
                  <PlusIcon className='size-5' />
                  Add Account
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      )}

      {data && data.length > 0 ? (
        <div className='space-y-4 mt-4'>
          <Heading icon={<BuildingOfficeIcon className='size-7' />}>
            Assigned Accounts ({data.length})
          </Heading>
          <div className='grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6'>
            {data.map(value => (
              <AccountSiteStaffMemberForm
                hideRemove={data?.length <= 1}
                key={value.key}
                data={value}
              />
            ))}
            {newAccount && (
              <AccountSiteStaffMemberForm
                data={newAccount}
                onSave={() => setNewAccount(null)}
                onRemove={() => setNewAccount(null)}
              />
            )}
          </div>
        </div>
      ) : (
        <div className='bg-white rounded-xl shadow-md border border-gray-300 p-12 text-center'>
          <BuildingOfficeIcon className='size-16 text-gray-300 mx-auto mb-4' />
          <Subheading className='mb-2'>No Account Access</Subheading>
          <Text className='text-gray-500'>
            This staff member doesn't have access to any accounts yet.
            {canAddAccounts && ' Add an account above to get started.'}
          </Text>
        </div>
      )}

      {previousStep && (
        <div className='flex justify-start pt-4'>
          <Button color='default' onPress={previousStep}>
            <ArrowLeftIcon />
            Back
          </Button>
        </div>
      )}
    </div>
  );
};

export default StaffAccounts;
